import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { GUIDES } from "../data/guides.js";
import GuideModal from "../components/GuideModal.jsx";

const LEVELS = [
  {
    id: "budget-commander",
    number: "01",
    title: "Budget Commander",
    subtitle: "Plan a zero-based budget, then live it out",
    accent: "magenta",
    unlocked: true,
    route: "/games/budget-commander",
  },
  {
    id: "time-raid",
    number: "02",
    title: "Time Raid",
    subtitle: "Defend your calendar from time-wasters",
    accent: "cyan",
    unlocked: true,
    route: "/games/time-raid",
  },
  {
    id: "compound-quest",
    number: "03",
    title: "Compound Quest",
    subtitle: "Grow a stack with the power of interest",
    accent: "gold",
    unlocked: true,
    route: "/games/compound-quest",
  },
  {
    id: "property-tycoon",
    number: "04",
    title: "Property Tycoon",
    subtitle: "Flip land, buy a rental, or house hack your way in",
    accent: "lime",
    unlocked: true,
    route: "/games/property-tycoon",
  },
  {
    id: "bond-bastion",
    number: "05",
    title: "Bond Bastion",
    subtitle: "Build a bond portfolio that can take a hit",
    accent: "cyan",
    unlocked: true,
    route: "/games/bond-bastion",
  },
  {
    id: "wildcat-portfolio",
    number: "06",
    title: "Wildcat Portfolio",
    subtitle: "Real estate, gold, crypto, and business — beyond the basics",
    accent: "magenta",
    unlocked: true,
    route: "/games/wildcat-portfolio",
  },
  {
    id: "off-the-clock",
    number: "07",
    title: "Off The Clock",
    subtitle: "Turn spare hours into something that pays off without you",
    accent: "gold",
    unlocked: true,
    route: "/games/off-the-clock",
  },
  {
    id: "inner-citadel",
    number: "08",
    title: "The Inner Citadel",
    subtitle: "Train the gap between what happens and how you respond",
    accent: "lime",
    unlocked: true,
    route: "/games/inner-citadel",
  },
  {
    id: "signal-boost",
    number: "09",
    title: "Signal Boost",
    subtitle: "Tune your voice, structure your point, hold the room",
    accent: "magenta",
    unlocked: true,
    route: "/games/signal-boost",
  },
  {
    id: "moat-runner",
    number: "10",
    title: "Moat Runner",
    subtitle: "Spot the boring business empires everyone else scrolls past",
    accent: "gold",
    unlocked: true,
    route: "/games/moat-runner",
  },
  {
    id: "the-standoff",
    number: "11",
    title: "The Standoff",
    subtitle: "Hold your nerve, read the table, close the deal",
    accent: "cyan",
    unlocked: true,
    route: "/games/the-standoff",
  },
  {
    id: "budget-blitz",
    number: "12",
    title: "Budget Blitz",
    subtitle: "Sort needs, wants, and savings before the timer runs dry",
    accent: "lime",
    unlocked: true,
    route: "/games/budget-blitz",
  },
  {
    id: "market-pulse",
    number: "13",
    title: "Market Pulse",
    subtitle: "Read the tape and call buy, hold, or sell before it moves on you",
    accent: "gold",
    unlocked: true,
    route: "/games/market-pulse",
  },
  {
    id: "scam-spotter",
    number: "14",
    title: "Scam Spotter",
    subtitle: "Flag the scam before it slips by",
    accent: "magenta",
    unlocked: true,
    route: "/games/scam-spotter",
  },
  {
    id: "cash-cascade",
    number: "15",
    title: "Cash Cascade",
    subtitle: "Chase bumpers, targets, and the jackpot before your last ball drains",
    accent: "gold",
    unlocked: true,
    route: "/games/cash-cascade",
  },
];

const accentClasses = {
  magenta: "border-magenta text-magenta hover:shadow-glow-magenta",
  cyan: "border-cyan text-cyan hover:shadow-glow-cyan",
  gold: "border-gold text-gold hover:shadow-glow-gold",
  lime: "border-lime text-lime hover:shadow-glow-lime",
};

export default function Dashboard() {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [guideId, setGuideId] = useState(null);

  const displayName = profile?.display_name || user?.email?.split("@")[0] || "Player";

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="mb-10">
        <p className="label-pixel mb-2 text-mist/70">Welcome back</p>
        <h1 className="pixel-heading text-xl text-mist sm:text-3xl">
          {displayName}<span className="cursor-blink" />
        </h1>
        <p className="mt-3 max-w-xl text-sm text-mist/70">
          Pick a level. Every level teaches one real skill — time, budgeting, investing, real
          estate, bonds, alternative assets, building leverage, or handling pressure — disguised
          as a game you'll actually want to beat.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {LEVELS.map((level) => (
          <div
            key={level.id}
            className={`panel relative flex flex-col items-start gap-3 p-5 text-left transition-transform ${
              level.unlocked ? "hover:-translate-y-1" : "opacity-50"
            }`}
          >
            <span className={`font-display text-[10px] ${accentClasses[level.accent]}`}>
              LEVEL {level.number}
            </span>
            <h2 className="pixel-heading text-sm text-mist">{level.title}</h2>
            <p className="text-xs text-mist/60">{level.subtitle}</p>

            <div className="mt-2 flex w-full gap-2">
              <button
                disabled={!level.unlocked}
                onClick={() => level.unlocked && navigate(level.route)}
                className={`flex-1 border-2 px-3 py-1.5 font-display text-[8px] uppercase transition-colors ${
                  level.unlocked ? accentClasses[level.accent] : "cursor-not-allowed border-mist/20 text-mist/30"
                }`}
              >
                {level.unlocked ? "Play ▶" : "🔒 Locked"}
              </button>

              {GUIDES[level.id] && (
                <button
                  onClick={() => setGuideId(level.id)}
                  className="border-2 border-mist/30 px-3 py-1.5 font-display text-[8px] uppercase text-mist/60 transition-colors hover:border-cyan hover:text-cyan"
                >
                  📖 Guide
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 panel p-5">
        <p className="label-pixel mb-2 text-mist/70">Coming soon</p>
        <p className="text-sm text-mist/70">
          More premium modules are next. In the meantime, check the 🏆 in the top bar to see how
          your family's scores stack up. Your current progress is always saved to your profile.
        </p>
      </div>

      <GuideModal guide={guideId ? GUIDES[guideId] : null} onClose={() => setGuideId(null)} />
    </div>
  );
}
